import React, { useState, useEffect } from 'react';
import { submitContactForm } from '../../services/contactApi';
import { 
  Mail, Phone, MapPin, Clock, Send, Loader2, CheckCircle, AlertTriangle,
  MessageCircle, Globe, Award, Users, ArrowRight, Sparkles, BookOpen, Target, Compass
} from 'lucide-react';

// Import Theme Context
import { useTheme } from '../Home/ThemeContext';

export default function ContactPage() {
  const context = useTheme();
  const isDarkMode = context.isDarkMode || context.theme === 'dark';

  const [formData, setFormData] = useState({
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '', 
    subject: '', 
    country: '', 
    message: '',
    // Ad and campaign tracking fields
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    gclid: '', // Google Click ID
    fbclid: '', // Facebook Click ID
    ad_url: '' // Source Page URL
  });

  const [otherSubject, setOtherSubject] = useState('');
  const [isFormOnlyView, setIsFormOnlyView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // --- AD TRACKING & ROUTING LOGIC ---
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    
    // Check if the link is specifically generated for a form-only view (?view=form-only)
    if (searchParams.get('view') === 'form-only') {
      setIsFormOnlyView(true);
    }

    // Capture URL query parameters for Ad campaigns
    const trackingData = {
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || '',
      gclid: searchParams.get('gclid') || '',
      fbclid: searchParams.get('fbclid') || '',
      ad_url: window.location.href
    };

    setFormData(prev => ({
      ...prev,
      ...trackingData
    }));
  }, []);

  // --- OFFICE STATUS LOGIC ---
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getOfficeStatus = () => {
    const options = { timeZone: 'Asia/Kolkata', hour12: false, hour: '2-digit', minute: '2-digit', weekday: 'long' };
    const istTimeStr = new Intl.DateTimeFormat('en-US', options).format(new Date());
    const [day, time] = istTimeStr.split(' ');
    const hour = parseInt(time.split(':')[0]);

    const isSunday = day === 'Sunday';
    const isOpen = isSunday 
      ? (hour >= 10 && hour < 18) 
      : (hour >= 9 && hour < 20);

    return {
      isOpen,
      text: isOpen ? 'Open Now' : 'Closed Now',
      color: isOpen ? 'text-green-500' : 'text-red-500',
      dot: isOpen ? 'bg-green-500' : 'bg-red-500'
    };
  };

  const status = getOfficeStatus();

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Prepare final payload data overriding custom 'Other' subject field if chosen
    const submissionData = { ...formData };
    if (submissionData.subject === 'Others' && otherSubject.trim() !== '') {
      submissionData.subject = `Others: ${otherSubject.trim()}`;
    }

    try {
      await submitContactForm(submissionData);
      
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'contact_form_submission',
          campaign: submissionData.utm_campaign,
          source: submissionData.utm_source,
          subject: submissionData.subject
        });
      }

      setSuccess(true);
      setFormData({ 
        firstName: '', lastName: '', email: '', phone: '', subject: '', country: '', message: '',
        utm_source: formData.utm_source, utm_medium: formData.utm_medium, utm_campaign: formData.utm_campaign,
        gclid: formData.gclid, fbclid: formData.fbclid, ad_url: formData.ad_url
      });
      setOtherSubject('');
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const highlights = [
    { icon: Compass, title: "Expert Guidance", desc: "Admissions, counselling, and coaching across all sectors.", color: "from-blue-500 to-indigo-600" },
    { icon: Globe, title: "Personalised Solutions", desc: "Tailored strategies for global learning pathways.", color: "from-purple-500 to-pink-600" },
    { icon: Target, title: "Focused Mentorship", desc: "Goal-driven education with dedicated mentor support.", color: "from-orange-500 to-red-600" }
  ];

  const contactInfo = [
    { icon: Mail, title: 'Email Us', value: 'info@vidyazon.com', subtitle: 'Reply within 24 hours', href: 'mailto:info@vidyazon.com', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Phone, title: 'Call Us', value: '+91 9997517305', subtitle: '24/7 Support', href: 'tel:+91 9997517305', gradient: 'from-purple-500 to-pink-500' },
    { icon: MapPin, title: 'Visit Us', value: 'Global Head Office', subtitle: 'New Delhi, India', gradient: 'from-orange-500 to-red-500' }
  ];

  const styles = {
    bg: isDarkMode ? 'bg-[#030712]' : 'bg-gray-50',
    card: isDarkMode ? 'bg-gray-900/50 border-gray-800 shadow-2xl' : 'bg-white border-gray-100 shadow-xl',
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    subText: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    input: isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
  };

  // --- CONDITIONALLY RENDER THE UNIQUE AD-ONLY LINK VIEW ---
  if (isFormOnlyView) {
    return (
      <div className={`min-h-screen ${styles.bg} transition-colors duration-500 py-10 px-4 flex items-center justify-center selection:bg-blue-500/30`}>
        <div className="w-full max-w-3xl">
          <div className={`p-6 md:p-12 rounded-3xl border backdrop-blur-md ${styles.card}`}>
            {success ? (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className={`text-3xl font-black mb-4 ${styles.text}`}>Application Received!</h2>
                <p className={`mb-8 ${styles.subText}`}>Our expert counselors will contact you within 24 business hours.</p>
                <button onClick={() => setSuccess(false)} className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold w-full sm:w-auto">Send Another Message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className={`text-3xl font-black ${styles.text}`}>Quick Inquiry</h2>
                  <p className={`text-sm mt-1 ${styles.subText}`}>Fill out the form below to get instant expert guidance.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>First Name</label>
                    <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="Jane" />
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Last Name</label>
                    <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="Doe" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Email</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="jane@example.com" />
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Phone Number</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="+91 00000 00000" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Interested Country</label>
                    <select name="country" required value={formData.country} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`}>
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="USA">USA</option>
                      <option value="UK">UK</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Germany">Germany</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Subject</label>
                    <select name="subject" required value={formData.subject} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`}>
                      <option value="">Select Service</option>
                      <option value="Admission Guidance">Admission Guidance</option>
                      <option value="Expert Counselling">Expert Counselling</option>
                      <option value="Partnership assistance">Partnership assistance</option>
                      <option value="Coaching Guidance">Coaching Guidance</option>
                      <option value="College & University Collaboration">College & University Collaboration</option>
                      <option value="School Collaboration">School Collaboration</option>
                      <option value="Training & Certification Collaboration">Training & Certification Collaboration</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>

                {/* Conditional custom field when "Others" is picked */}
                {formData.subject === 'Others' && (
                  <div className="space-y-2 transition-all duration-300">
                    <label className={`text-sm font-bold ${styles.subText}`}>Please specify your requirements</label>
                    <input type="text" required value={otherSubject} onChange={(e) => setOtherSubject(e.target.value)} className={`w-full p-4 rounded-xl border-2 outline-none border-blue-500 transition-all ${styles.input}`} placeholder="E.g., Special Event Collaboration / Placement Inquiry" />
                  </div>
                )}

                <div className="space-y-2">
                  <label className={`text-sm font-bold ${styles.subText}`}>Message</label>
                  <textarea name="message" rows="4" required value={formData.message} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all resize-none ${styles.input}`} placeholder="How can we help you?" />
                </div>

                {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl flex items-center gap-3"><AlertTriangle className="w-5 h-5 flex-shrink-0" /> {error}</div>}

                <button type="submit" disabled={loading} className="w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-black text-lg shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Send className="w-5 h-5" /> Send Inquiry <ArrowRight className="w-5 h-5" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- DEFAULT MAIN PAGE VIEW ---
  return (
    <div className={`min-h-screen ${styles.bg} transition-colors duration-500 selection:bg-blue-500/30`}>
      
      {/* Background Decor */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-1/4 w-96 h-96 rounded-full blur-[120px] opacity-20 ${isDarkMode ? 'bg-blue-600' : 'bg-blue-300'}`} />
        <div className={`absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-[120px] opacity-20 ${isDarkMode ? 'bg-purple-600' : 'bg-purple-300'}`} />
      </div>

      <div className="relative z-10 pt-32 pb-20 px-4">
        {/* --- HEADER --- */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/20 mb-6 font-bold text-sm">
            <Sparkles className="w-4 h-4" /> REALIZE YOUR DREAMS
          </div>
          <h1 className={`text-4xl sm:text-5xl md:text-7xl font-black mb-6 tracking-tight ${styles.text}`}>
            Connect with <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Vidyazon</span>
          </h1>
          <p className={`text-base sm:text-lg md:text-xl max-w-2xl mx-auto ${styles.subText}`}>
            Empowering your global education journey through precision mentorship and expert guidance.
          </p>
        </div>

        {/* --- HIGHLIGHT CARDS --- */}
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-20">
          {highlights.map((item, i) => (
            <div key={i} className={`group p-6 sm:p-8 rounded-3xl border transition-all duration-300 hover:-translate-y-2 ${styles.card}`}>
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                <item.icon className="text-white w-7 h-7" />
              </div>
              <h3 className={`text-xl font-bold mb-3 ${styles.text}`}>{item.title}</h3>
              <p className={styles.subText}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* --- MAIN SECTION --- */}
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-8">
          
          {/* LEFT: INFO & LIVE CLOCK */}
          <div className="lg:col-span-4 space-y-6">
            <div className={`p-6 sm:p-8 rounded-3xl border backdrop-blur-md ${styles.card}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`font-bold ${styles.text}`}>Office Hours</h3>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full animate-pulse ${status.dot}`} />
                  <span className={`text-sm font-bold ${status.color}`}>{status.text}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24 rounded-full border-4 border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Clock className={`w-12 h-12 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin-slow" />
                </div>
                <div>
                  <p className={`text-xl sm:text-2xl font-black ${styles.text}`}>
                    {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Local Time (IST)</p>
                </div>
              </div>

              <div className="mt-8 space-y-3 pt-6 border-t border-gray-100/10">
                <div className="flex justify-between text-sm">
                  <span className={styles.subText}>Mon - Sat</span>
                  <span className={`font-bold ${styles.text}`}>9:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={styles.subText}>Sunday</span>
                  <span className={`font-bold ${styles.text}`}>10:00 AM - 6:00 PM</span>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 lg:grid-cols-1 gap-4">
              {contactInfo.map((info, idx) => (
                <a key={idx} href={info.href} className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:scale-[1.02] ${styles.card}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center text-white flex-shrink-0`}>
                    <info.icon className="w-5 h-5" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs font-bold text-gray-500 uppercase truncate">{info.title}</p>
                    <p className={`font-bold ${styles.text} text-sm sm:text-base truncate`}>{info.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* RIGHT: CONTACT FORM */}
          <div className="lg:col-span-8">
            <div className={`p-6 md:p-12 rounded-3xl border backdrop-blur-md ${styles.card}`}>
              {success ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h2 className={`text-3xl font-black mb-4 ${styles.text}`}>Application Received!</h2>
                  <p className={`mb-8 ${styles.subText}`}>Our expert counselors will contact you within 24 business hours.</p>
                  <button onClick={() => setSuccess(false)} className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold w-full sm:w-auto">Send Another Message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>First Name</label>
                      <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="Jane" />
                    </div>
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>Last Name</label>
                      <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="Doe" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>Work Email</label>
                      <input type="email" name="email" required value={formData.email} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="jane@example.com" />
                    </div>
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>Phone Number</label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`} placeholder="+91 00000 00000" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>Interested Country</label>
                      <select name="country" required value={formData.country} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`}>
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="UK">UK</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className={`text-sm font-bold ${styles.subText}`}>Subject</label>
                      <select name="subject" required value={formData.subject} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all ${styles.input}`}>
                        <option value="">Select Service</option>
                        <option value="Admission Guidance">Admission Guidance</option>
                        <option value="Expert Counselling">Expert Counselling</option>
                        <option value="Partnership assistance">Partnership assistance</option>
                        <option value="Coaching Guidance">Coaching Guidance</option>
                        <option value="College & University Collaboration">College & University Collaboration</option>
                        <option value="School Collaboration">School Collaboration</option>
                        <option value="Training & Certification Collaboration">Training & Certification Collaboration</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                  </div>

                  {/* Conditional custom field when "Others" is picked */}
                  {formData.subject === 'Others' && (
                    <div className="space-y-2 transition-all duration-300">
                      <label className={`text-sm font-bold ${styles.subText}`}>Please specify your requirements</label>
                      <input type="text" required value={otherSubject} onChange={(e) => setOtherSubject(e.target.value)} className={`w-full p-4 rounded-xl border-2 outline-none border-blue-500 transition-all ${styles.input}`} placeholder="E.g., Special Event Collaboration / Placement Inquiry" />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className={`text-sm font-bold ${styles.subText}`}>Message</label>
                    <textarea name="message" rows="4" required value={formData.message} onChange={handleChange} className={`w-full p-4 rounded-xl border-2 outline-none focus:border-blue-500 transition-all resize-none ${styles.input}`} placeholder="How can we help you?" />
                  </div>

                  {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl flex items-center gap-3"><AlertTriangle className="w-5 h-5 flex-shrink-0" /> {error}</div>}

                  <button type="submit" disabled={loading} className="w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-black text-lg shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50">
                    {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Send className="w-5 h-5" /> Send Inquiry <ArrowRight className="w-5 h-5" /></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
}